import { useMemo } from 'react';

import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetRopaApi from 'config/api/assetRopa/assetRopa';
import type { OwnerAssetRopaListResponse, OwnerAssetRopaMetrics } from 'config/api/assetRopa/assetRopa.types';
import type { SoftDeleteProcessingActivityInput } from 'config/api/assetRopa/assetRopaWorkspace.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import ropaReviewsApi from 'config/api/ropaReviews/ropaReviews';
import type { ActiveRopaReviewResponse, AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import type { AssetRopaPageVariant } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';

import type { AssetRopaReviewFilters } from './assetRopaReview.types';
import { assetRopaReviewFiltersToQueryParams, buildAssetRopaReviewFilterOptions, filterAssetRopaReviews } from './assetRopaReviewFilters';

type UseAssetRopaReviewPageOptions = {
  filters: AssetRopaReviewFilters;
  organizationId?: number;
  variant?: AssetRopaPageVariant;
};

const buildOwnerActiveReview = (
  ownerList: OwnerAssetRopaListResponse | undefined,
  metrics: OwnerAssetRopaMetrics | null | undefined,
): ActiveRopaReviewResponse | null => {
  if (!ownerList?.activeReview) {
    return null;
  }

  const resolvedMetrics = metrics !== undefined ? metrics : ownerList.metrics;

  return {
    review: {
      id: ownerList.activeReview.id,
      status: ownerList.activeReview.status,
      initiatedAt: ownerList.activeReview.initiatedAt,
      deadline: ownerList.activeReview.deadline,
      reviewInstructions: ownerList.activeReview.reviewInstructions,
      initiatedByMembershipId: ownerList.activeReview.initiatedByMembershipId,
      initiatedByDisplayName: ownerList.activeReview.initiatedByDisplayName,
    },
    metrics: resolvedMetrics,
    actions: {
      canEditDetails: false,
      canCompleteReview: false,
      canCancelReview: false,
    },
  };
};

const useAssetRopaReviewPage = ({ filters, organizationId, variant = 'admin' }: UseAssetRopaReviewPageOptions) => {
  const queryClient = useQueryClient();
  const isOwnerVariant = variant === 'owner';
  const filterParams = assetRopaReviewFiltersToQueryParams(filters);

  /**
   * Owner ROPA review is scoped exclusively through asset-ropa/mine* endpoints.
   * Do not call admin ropa-reviews/.../assets/ and filter client-side.
   */
  const ownerMineQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE, organizationId, filterParams],
    async () => {
      const { data } = await assetRopaApi.getMine(organizationId as number, filterParams);
      return data;
    },
    {
      enabled: Boolean(organizationId && isOwnerVariant),
      retry: false,
      keepPreviousData: true,
    },
  );

  const ownerMetricsQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE_METRICS, organizationId],
    async () => {
      const { data } = await assetRopaApi.getMineMetrics(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId && isOwnerVariant),
      retry: false,
      keepPreviousData: true,
    },
  );

  const activeReviewQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_ACTIVE, organizationId, variant],
    async () => {
      const { data } = await ropaReviewsApi.getActiveReview(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId && !isOwnerVariant),
      retry: false,
    },
  );

  const reviewRoundId = isOwnerVariant ? ownerMineQuery.data?.activeReview?.id : activeReviewQuery.data?.review.id;

  const assetReviewsQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_ASSETS, organizationId, reviewRoundId, filterParams, variant],
    async () => {
      const { data } = await ropaReviewsApi.getAssetReviews(organizationId as number, reviewRoundId as number, filterParams);
      return data;
    },
    {
      enabled: Boolean(organizationId && !isOwnerVariant && reviewRoundId),
      retry: false,
      keepPreviousData: true,
    },
  );

  const activeReview: ActiveRopaReviewResponse | null = isOwnerVariant
    ? buildOwnerActiveReview(ownerMineQuery.data, ownerMetricsQuery.data !== undefined ? ownerMetricsQuery.data.reviewMetrics : undefined)
    : activeReviewQuery.data ?? null;

  const allAssetReviews: AssetRopaReview[] = isOwnerVariant
    ? ((ownerMineQuery.data?.assets ?? []) as AssetRopaReview[])
    : assetReviewsQuery.data ?? [];

  const filteredAssetReviews = useMemo(() => filterAssetRopaReviews(allAssetReviews, filters), [allAssetReviews, filters]);

  const filterOptions = useMemo(() => buildAssetRopaReviewFilterOptions(allAssetReviews), [allAssetReviews]);

  const invalidateReviewQueries = async () => {
    await queryClient.invalidateQueries([QUERY_KEYS.ROPA_REVIEWS_ACTIVE, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ROPA_REVIEWS_ASSETS, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE_METRICS, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE_DETAIL, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_AVAILABLE_PROCESSING_ACTIVITIES, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_WORKSPACE, organizationId]);
  };

  const acceptProcessingReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_ACCEPT_PROCESSING_REVIEW,
    async (reviewRowId: number) => {
      await ropaReviewsApi.acceptAssetProcessingActivityReview(organizationId as number, reviewRowId);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const requestClarificationMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_REQUEST_CLARIFICATION,
    async ({ comment, reviewRowId }: { comment: string; reviewRowId: number }) => {
      await ropaReviewsApi.requestAssetProcessingActivityReviewClarification(organizationId as number, reviewRowId, { comment });
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const bulkAcceptMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_BULK_ACCEPT_SUBMITTED,
    async (assetReviewId: number) => {
      await ropaReviewsApi.bulkAcceptSubmitted(organizationId as number, reviewRoundId as number, assetReviewId);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const addProcessingActivityMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_ADD_PROCESSING_ACTIVITY,
    async ({ assetRopaId, processingActivityId }: { assetRopaId: number; processingActivityId: number }) => {
      const { data } = await assetRopaApi.addProcessingActivity(organizationId as number, assetRopaId, { processingActivityId });
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const softDeleteMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_SOFT_DELETE_APA,
    async ({ apaId, assetRopaId, payload }: { apaId: number; assetRopaId: number; payload: SoftDeleteProcessingActivityInput }) => {
      const { data } = await assetRopaApi.softDeleteProcessingActivity(organizationId as number, assetRopaId, apaId, payload);
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const updateReviewDetailsMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_UPDATE_DETAILS,
    async (payload: { deadline: string; reviewInstructions: string }) => {
      await ropaReviewsApi.updateReviewDetails(organizationId as number, reviewRoundId as number, payload);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const completeReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_COMPLETE,
    async (completionComment: string) => {
      const { data } = await ropaReviewsApi.completeReview(organizationId as number, reviewRoundId as number, {
        completionComment,
      });
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const cancelReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_CANCEL,
    async (cancellationComment: string) => {
      const { data } = await ropaReviewsApi.cancelReview(organizationId as number, reviewRoundId as number, {
        cancellationComment,
      });
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const confirmNoProcessingActivitiesMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_CONFIRM_NO_PROCESSING_ACTIVITIES,
    async (assetRopaId: number) => {
      await assetRopaApi.confirmNoProcessingActivities(organizationId as number, assetRopaId);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const cancelIncompleteAssetReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_CANCEL_INCOMPLETE_ASSET_REVIEW,
    async ({ assetReviewId, cancellationComment }: { assetReviewId: number; cancellationComment: string }) => {
      await ropaReviewsApi.cancelIncompleteAssetReview(organizationId as number, reviewRoundId as number, assetReviewId, {
        cancellationComment,
      });
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const adminListError = activeReviewQuery.isError || assetReviewsQuery.isError;
  const listError = isOwnerVariant ? ownerMineQuery.isError : adminListError;

  const isForbidden = Boolean(
    listError &&
      ((ownerMineQuery.isError && axios.isAxiosError(ownerMineQuery.error) && ownerMineQuery.error.response?.status === 403) ||
        (activeReviewQuery.isError && axios.isAxiosError(activeReviewQuery.error) && activeReviewQuery.error.response?.status === 403) ||
        (assetReviewsQuery.isError && axios.isAxiosError(assetReviewsQuery.error) && assetReviewsQuery.error.response?.status === 403)),
  );

  const isLoading = isOwnerVariant
    ? ownerMineQuery.isLoading
    : activeReviewQuery.isLoading || (Boolean(organizationId && reviewRoundId) && assetReviewsQuery.isLoading);

  const isError = listError && !isForbidden;

  return {
    acceptProcessingReviewMutation,
    activeReview,
    addProcessingActivityMutation,
    allAssetReviews,
    assetReviewsQuery: isOwnerVariant ? ownerMineQuery : assetReviewsQuery,
    bulkAcceptMutation,
    cancelReviewMutation,
    cancelIncompleteAssetReviewMutation,
    completeReviewMutation,
    confirmNoProcessingActivitiesMutation,
    filterOptions,
    filteredAssetReviews,
    invalidateReviewQueries,
    isError,
    isForbidden,
    isLoading,
    refetch: async () => {
      if (isOwnerVariant) {
        await Promise.all([ownerMineQuery.refetch(), ownerMetricsQuery.refetch()]);
        return;
      }

      await activeReviewQuery.refetch();
      await assetReviewsQuery.refetch();
    },
    requestClarificationMutation,
    reviewRoundId,
    softDeleteMutation,
    updateReviewDetailsMutation,
    variant,
  };
};

export default useAssetRopaReviewPage;
